import { Controller } from '@hotwired/stimulus'
import { DirectUpload } from '@rails/activestorage'

// Connects to data-controller="simple-dropzone"
export default class extends Controller {
  static targets = ['input', 'preview', 'submit', 'selectButton']
  static values = {
    maxFileSize: { type: Number, default: 5 },
    acceptedTypes: { type: Array, default: ['application/pdf', 'image/jpeg', 'image/png'] }
  }

  connect() {
    this.files = []
    this.fileElements = new Map()
    this.bindEvents()
    this.initializeSubmitButton()
  }

  initializeSubmitButton() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
    }
  }

  bindEvents() {
    // Drag and drop events
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      this.element.addEventListener(eventName, this.preventDefaults, false)
    })

    this.element.addEventListener('drop', this.handleDrop.bind(this), false)
    this.element.addEventListener('dragover', this.highlight.bind(this), false)
    this.element.addEventListener('dragleave', this.unhighlight.bind(this), false)

    // File input change
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener('change', this.handleFiles.bind(this))
    }
  }

  preventDefaults(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  highlight(e) {
    if (this.files.length === 0) {
      this.element.classList.add('bg-blue-50', 'border-blue-300')
    }
  }

  unhighlight(e) {
    this.element.classList.remove('bg-blue-50', 'border-blue-300')
  }

  handleDrop(e) {
    this.unhighlight(e)
    if (this.files.length === 0) {
      const files = e.dataTransfer.files
      this.handleFiles({ target: { files } })
    }
  }

  handleFiles(e) {
    const files = [...e.target.files]
    
    if (files.length > 0 && this.files.length === 0) {
      this.uploadFile(files[0])
    }

    e.target.value = ''
  }

  triggerFileInput() {
    if (this.files.length > 0) {
      return
    }
    
    if (this.hasInputTarget) {
      this.inputTarget.click()
    } else {
      const fileInput = this.element.querySelector('input[type="file"]')
      if (fileInput) {
        fileInput.click()
      }
    }
  }

  uploadFile(file) {
    if (!this.validateFile(file)) return

    const upload = new DirectUpload(file, this.inputTarget.dataset.directUploadUrl, this)

    this.showProgress(file)

    upload.create((error, blob) => {
      if (error) {
        this.showError(file, error)
      } else {
        this.showSuccess(file, blob)
        this.files = [blob.signed_id]
        this.updateHiddenField()
        this.updateUIState()
      }
    })
  }

  validateFile(file) {
    // Check file size
    if (file.size > this.maxFileSizeValue * 1024 * 1024) {
      alert(`El archivo ${file.name} es muy grande. Máximo ${this.maxFileSizeValue}MB`)
      return false
    }

    // Check file type
    if (!this.acceptedTypesValue.includes(file.type)) {
      alert(`Tipo de archivo no permitido: ${file.type}`)
      return false
    }

    return true
  }

  showProgress(file) {
    const preview = document.createElement('div')
    preview.className = 'border rounded p-2 mb-2'
    preview.innerHTML = `
      <div class="flex items-center justify-between">
        <span class="text-sm truncate">${file.name}</span>
        <div class="w-24 bg-gray-200 rounded-full h-2">
          <div class="bg-blue-600 h-2 rounded-full transition-all duration-300" style="width: 0%"></div>
        </div>
      </div>
    `
    this.previewTarget.appendChild(preview)
    this.fileElements.set(file.name, preview)
  }

  showSuccess(file, blob) {
    const preview = this.fileElements.get(file.name)
    if (!preview) return

    const progressBar = preview.querySelector('.bg-blue-600')
    progressBar.style.width = '100%'

    const container = preview.querySelector('.flex')
    container.innerHTML = `
      <span class="text-sm truncate text-green-600">✓ ${file.name}</span>
      <button type="button" class="text-red-500 text-sm cursor-pointer" data-action="click->simple-dropzone#removeFile" data-signed-id="${blob.signed_id}" data-filename="${file.name}">
        Eliminar
      </button>
    `
  }

  showError(file, error) {
    const preview = this.fileElements.get(file.name)
    if (!preview) return

    const container = preview.querySelector('.flex')
    container.innerHTML = `
      <span class="text-sm truncate text-red-600">✗ ${file.name} - Error</span>
      <span class="text-xs text-red-500">${error}</span>
    `
  }

  removeFile(e) {
    const signedId = e.target.dataset.signedId
    const filename = e.target.dataset.filename
    
    this.files = this.files.filter(id => id !== signedId)
    
    this.fileElements.delete(filename)
    
    e.target.closest('.border').remove()
    
    this.updateHiddenField()
    this.updateUIState()
  }

  updateUIState() {
    const hasFile = this.files.length > 0
    
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = !hasFile
    }
    
    if (this.hasSelectButtonTarget) {
      this.selectButtonTarget.disabled = hasFile
    }
  }

  updateHiddenField() {
    this.element.querySelectorAll('input[name="complementary_file[files][]"][type="hidden"]').forEach(field => field.remove())

    this.files.forEach(signedId => {
      const hiddenField = document.createElement('input')
      hiddenField.type = 'hidden'
      hiddenField.name = 'complementary_file[files][]'
      hiddenField.value = signedId
      this.element.appendChild(hiddenField)
    })

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = this.files.length === 0
    }
  }

  directUploadWillStoreFileWithXHR(xhr) {
  }
}
